import { Component, ElementRef, Input, OnChanges, SimpleChanges, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventEmitter, Output } from '@angular/core';

import { StoreService } from '../../services/store.service';
import { BusinessLogicService } from '../../services/business-logic.service';
import { ApiService } from '../../services/api.service';
import { ComponentContextMenuRequest, SmartPdfUploadResult, TelesisNetlistUploadResult, WorksheetRow } from '../../models';

interface WorksheetFailureModeRollup {
  modeCode: string;
  modeName: string;
  assignedRow: WorksheetRow | null;
}

interface WorksheetComponentRollup {
  componentDesignator: string;
  componentType: string;
  hasType: boolean;
  totalModes: number;
  assignedModes: number;
  status: 'white' | 'amber' | 'green';
  operatingTemp: number;
  modeRows: WorksheetFailureModeRollup[];
}

@Component({
  selector: 'app-worksheet-view',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './worksheet-view.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './worksheet-view.component.css' // Or whatever style URL this needs
})
export class WorksheetViewComponent {
  private readonly store = inject(StoreService);
  private readonly businessLogic = inject(BusinessLogicService);
  private readonly apiService = inject(ApiService);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private pendingLocalFocusDesignator: string | null = null;

  @Input() focusedComponentDesignator = '';
  @Input() maxNetlistUploadSizeBytes = 10 * 1024 * 1024;
  @Input() maxSmartPdfUploadSizeBytes = 50 * 1024 * 1024;

  readonly filterForm = new FormGroup({
    q: new FormControl(this.store.searchQuery(), { nonNullable: true })
  });

  // Extract signals from Store/BusinessLogic
  readonly worksheetRows = this.businessLogic.worksheetRows;
  readonly worksheetSummary = this.businessLogic.worksheetSummary;
  readonly safetyGoals = this.store.safetyGoals;
  readonly selectedComponent = this.store.selectedComponent;
  readonly selectedWorksheetRowId = this.store.selectedWorksheetRowId;
  readonly error = this.store.error;
  readonly componentRollups = computed<WorksheetComponentRollup[]>(() => {
    const filter = this.store.searchQuery().trim().toLowerCase();
    const rows = this.worksheetRows().filter((row) => row.riskBand !== 'incomplete');
    const rowsByComponent = new Map<string, WorksheetRow[]>();
    for (const row of rows) {
      const existingRows = rowsByComponent.get(row.componentDesignator) ?? [];
      existingRows.push(row);
      rowsByComponent.set(row.componentDesignator, existingRows);
    }

    const typeByName = new Map(this.store.componentTypes().map((type) => [type.name, type]));

    return this.store.components()
      .map((component) => {
        const componentDesignator = component.component_designator;
        const componentType = component.component_type ?? 'Unassigned';
        const hasType = Boolean(component.component_type);
        const typeDef = component.component_type ? typeByName.get(component.component_type) : undefined;
        const potentialModes = typeDef?.failure_modes ?? [];
        const assignedRows = rowsByComponent.get(componentDesignator) ?? [];
        const assignedByMode = new Map<string, WorksheetRow>();

        for (const row of assignedRows) {
          if (!row.failureModeCode || row.failureModeCode === '-') {
            continue;
          }
          if (!assignedByMode.has(row.failureModeCode)) {
            assignedByMode.set(row.failureModeCode, row);
          }
        }

        const modeRows: WorksheetFailureModeRollup[] = potentialModes.map((mode) => ({
          modeCode: mode.code,
          modeName: mode.name,
          assignedRow: assignedByMode.get(mode.code) ?? null
        }));

        const totalModes = modeRows.length;
        const assignedModes = modeRows.filter((mode) => Boolean(mode.assignedRow)).length;

        let status: WorksheetComponentRollup['status'] = 'white';
        if (hasType) {
          status = totalModes > 0 && assignedModes === totalModes ? 'green' : 'amber';
        }

        if (!filter) {
          return {
            componentDesignator,
            componentType,
            hasType,
            totalModes,
            assignedModes,
            status,
            operatingTemp: component.op_temp ?? 0,
            modeRows
          };
        }

        const componentMatch = `${componentDesignator} ${componentType}`.toLowerCase().includes(filter);
        const matchingModeRows = modeRows.filter((mode) => {
          const assignedRow = mode.assignedRow;
          const modeSearch = `${mode.modeCode} ${mode.modeName} ${assignedRow?.failureName ?? ''} ${assignedRow?.failureEffect ?? ''} ${assignedRow?.systemEffect ?? ''}`.toLowerCase();
          return modeSearch.includes(filter);
        });

        if (!componentMatch && matchingModeRows.length === 0) {
          return null;
        }

        return {
          componentDesignator,
          componentType,
          hasType,
          totalModes,
          assignedModes,
          status,
          operatingTemp: component.op_temp ?? 0,
          modeRows: componentMatch ? modeRows : matchingModeRows
        };
      })
      .filter((rollup): rollup is WorksheetComponentRollup => Boolean(rollup))
      .sort((left, right) => left.componentDesignator.localeCompare(right.componentDesignator));
  });

  readonly expandedComponents = signal<Set<string>>(new Set());

  @Output() componentDesignatorSelected = new EventEmitter<string>();
  @Output() netlistUploadStarted = new EventEmitter<string>();
  @Output() netlistUploadSucceeded = new EventEmitter<TelesisNetlistUploadResult>();
  @Output() netlistUploadFailed = new EventEmitter<string>();
  @Output() smartPdfUploadStarted = new EventEmitter<string>();
  @Output() smartPdfUploadSucceeded = new EventEmitter<SmartPdfUploadResult>();
  @Output() smartPdfUploadFailed = new EventEmitter<string>();
  @Output() componentContextMenuRequested = new EventEmitter<ComponentContextMenuRequest>();

  ngOnChanges(changes: SimpleChanges): void {
    const focusedChange = changes['focusedComponentDesignator'];
    if (!focusedChange) {
      return;
    }

    const raw = (this.focusedComponentDesignator ?? '').trim();
    if (!raw) {
      return;
    }

    const candidates = this.toDesignatorCandidates(raw);
    const match = this.componentRollups().find((rollup) => candidates.has(this.normalizeDesignator(rollup.componentDesignator)));
    if (!match) {
      return;
    }

    const localFocusDesignator = this.pendingLocalFocusDesignator;
    if (localFocusDesignator && candidates.has(this.normalizeDesignator(localFocusDesignator))) {
      this.pendingLocalFocusDesignator = null;
      this.selectComponentRollup(match, false);
      return;
    }

    this.selectComponentRollup(match, false);
    this.scrollComponentIntoView(match.componentDesignator);
  }

  constructor() {
    this.filterForm.controls.q.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(val => {
        this.store.searchQuery.set(val);
      });
  }

  selectWorksheetRow(row: WorksheetRow, emitSelection = true): void {
    const isSameSelection =
      this.selectedWorksheetRowId() === row.failureInstanceId &&
      this.selectedComponent()?.component_designator === row.componentDesignator;

    if (isSameSelection) {
      if (emitSelection) {
        this.componentDesignatorSelected.emit(row.componentDesignator);
      }
      return;
    }

    this.store.selectedWorksheetRowId.set(row.failureInstanceId);
    this.expandComponent(row.componentDesignator);
    if (emitSelection) {
      this.componentDesignatorSelected.emit(row.componentDesignator);
    }
    this.loadComponentDetail(row.componentDesignator);
  }

  selectComponentRollup(rollup: WorksheetComponentRollup, emitSelection = true): void {
    this.selectComponentDesignator(rollup.componentDesignator, emitSelection);
  }

  onComponentRollupClick(rollup: WorksheetComponentRollup): void {
    this.selectComponentDesignator(rollup.componentDesignator);
  }

  onComponentRollupDoubleClick(rollup: WorksheetComponentRollup): void {
    this.selectComponentDesignator(rollup.componentDesignator);
    if (rollup.hasType && rollup.totalModes > 0) {
      this.expandComponent(rollup.componentDesignator);
    }
  }

  onRollupToggleClick(event: MouseEvent, rollup: WorksheetComponentRollup): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectComponentDesignator(rollup.componentDesignator);

    if (rollup.hasType && rollup.totalModes > 0) {
      this.toggleComponentExpansion(rollup.componentDesignator);
    }
  }

  selectFailureModeRow(rollup: WorksheetComponentRollup, mode: WorksheetFailureModeRollup): void {
    if (mode.assignedRow) {
      this.selectWorksheetRow(mode.assignedRow);
      return;
    }

    this.selectComponentDesignator(rollup.componentDesignator);
  }

  isExpanded(componentDesignator: string): boolean {
    return this.expandedComponents().has(componentDesignator);
  }

  isComponentRollupSelected(componentDesignator: string): boolean {
    const selectedRowId = this.selectedWorksheetRowId();
    if (selectedRowId) {
      const selectedRow = this.worksheetRows().find((row) => row.failureInstanceId === selectedRowId);
      if (selectedRow) {
        return selectedRow.componentDesignator === componentDesignator;
      }

      if (selectedRowId === `UNLINKED-${componentDesignator}`) {
        return true;
      }
    }

    return this.selectedComponent()?.component_designator === componentDesignator;
  }

  getRollupStatusLabel(rollup: WorksheetComponentRollup): string {
    if (rollup.status === 'white') {
      return 'Type unassigned';
    }

    if (rollup.status === 'green') {
      return 'Complete';
    }

    return 'Pending failure instances';
  }

  requestComponentContextMenu(event: MouseEvent, componentDesignator: string): void {
    event.preventDefault();
    this.selectComponentDesignator(componentDesignator);
    this.componentContextMenuRequested.emit({
      componentDesignator,
      clientX: event.clientX,
      clientY: event.clientY
    });
  }

  requestFailureModeContextMenu(event: MouseEvent, rollup: WorksheetComponentRollup, mode: WorksheetFailureModeRollup): void {
    event.preventDefault();
    this.selectComponentDesignator(rollup.componentDesignator);
    this.componentContextMenuRequested.emit({
      componentDesignator: rollup.componentDesignator,
      failureModeCode: mode.modeCode,
      clientX: event.clientX,
      clientY: event.clientY
    });
  }

  private normalizeDesignator(value: string): string {
    return value
      .toUpperCase()
      .replace(/\s+/g, '')
      .replace(/[^A-Z0-9]/g, '');
  }

  private toDesignatorCandidates(value: string): Set<string> {
    const normalized = this.normalizeDesignator(value);
    const candidates = new Set<string>([normalized]);

    const flippedMatch = normalized.match(/^(\d+)([A-Z]+)$/);
    if (flippedMatch) {
      candidates.add(`${flippedMatch[2]}${flippedMatch[1]}`);
    }

    const canonicalMatch = normalized.match(/^([A-Z]+)(\d+)$/);
    if (canonicalMatch) {
      candidates.add(`${canonicalMatch[2]}${canonicalMatch[1]}`);
    }

    return candidates;
  }

  private scrollComponentIntoView(componentDesignator: string): void {
    const escapedId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(componentDesignator) : componentDesignator;
    const rowElement = this.hostElement.nativeElement.querySelector(
      `.worksheet-table tbody tr[data-component-id="${escapedId}"]`
    );

    if (!(rowElement instanceof HTMLElement)) {
      return;
    }

    rowElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  private selectComponentDesignator(componentDesignator: string, emitSelection = true): void {
    const matchingRows = this.worksheetRows().filter((row) => row.componentDesignator === componentDesignator);
    const selectedRow = matchingRows.find((row) => row.failureInstanceId === this.selectedWorksheetRowId());
    const defaultRow = matchingRows.find((row) => row.riskBand !== 'incomplete') ?? matchingRows[0];
    this.store.selectedWorksheetRowId.set(selectedRow?.failureInstanceId ?? defaultRow?.failureInstanceId ?? `UNLINKED-${componentDesignator}`);

    if (emitSelection) {
      this.pendingLocalFocusDesignator = componentDesignator;
      this.componentDesignatorSelected.emit(componentDesignator);
    }

    this.loadComponentDetail(componentDesignator);
  }

  private toggleComponentExpansion(componentDesignator: string): void {
    const next = new Set(this.expandedComponents());
    if (next.has(componentDesignator)) {
      next.delete(componentDesignator);
    } else {
      next.add(componentDesignator);
    }
    this.expandedComponents.set(next);
  }

  private expandComponent(componentDesignator: string): void {
    const next = new Set(this.expandedComponents());
    next.add(componentDesignator);
    this.expandedComponents.set(next);
  }

  loadComponentDetail(componentDesignator: string): void {
    this.apiService.getComponentDetail(componentDesignator)
      .subscribe({
        next: (response) => {
          this.store.selectedComponent.set(response.data);
        },
        error: (err: unknown) => {
          this.store.error.set(err instanceof Error ? err.message : 'Failed to load component details.');
        }
      });
  }
}
